import { Paper, Avatar, Typography, Chip } from '@mui/material';
import { CheckCircle, Cancel, AccessTime, PersonOff } from '@mui/icons-material';
import { IAttendanceStudent } from 'services/SchoolClass/types';

interface StatusConfig {
  border: string;
  badge: "success" | "warning" | "default" | "error" | "info" | "primary" | "secondary";
  opacity: number;
  filter: string;
  icon: JSX.Element;
}

const STATUS_CONFIG: Record<"Presente" | "Ausente" | "Atrasado", StatusConfig> = {
  Presente: { border: 'success.main', badge: 'success', opacity: 1, filter: 'none', icon: <CheckCircle fontSize="small" /> },
  Ausente:  { border: 'divider',      badge: 'default', opacity: 0.6, filter: 'grayscale(100%)', icon: <Cancel fontSize="small" /> },
  Atrasado: { border: 'warning.main', badge: 'warning', opacity: 1, filter: 'none', icon: <AccessTime fontSize="small" /> },
};

export function StudentCard({ student }: { student: IAttendanceStudent }) {
  const isInactive = student.isActive === false;
  const cfg = STATUS_CONFIG[student.status];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        textAlign: 'center',
        height: 160,
        borderRadius: 3,
        bgcolor: isInactive ? 'transparent' : 'background.paper',
        border: isInactive ? '1px dashed' : '1px solid',
        borderColor: isInactive ? 'divider' : cfg.border,
        opacity: isInactive ? 0.4 : cfg.opacity,
        filter: isInactive ? 'grayscale(100%)' : cfg.filter,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          opacity: isInactive ? 0.6 : (student.status === 'Ausente' ? 0.7 : 1),
        }
      }}
    >
      <Avatar 
        src={student.avatar} 
        sx={{ 
          width: 48, 
          height: 48, 
          bgcolor: isInactive ? 'action.disabledBackground' : 'background.default', 
          border: '2px solid', 
          borderColor: 'divider',
          color: 'text.secondary',
          fontWeight: 'bold',
          flexShrink: 0 
        }}
      >
        {student.initials}
      </Avatar>

      <Typography 
        variant="caption" 
        fontWeight="bold" 
        color={isInactive ? "text.disabled" : "text.primary"}
        sx={{ 
          lineHeight: 1.2, 
          maxWidth: '100%',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          my: 1,
          textDecoration: isInactive ? 'line-through' : 'none'
        }}
      >
        {student.name}
      </Typography>

      <Chip 
        icon={isInactive ? <PersonOff fontSize="small" /> : cfg.icon}
        label={isInactive ? "Desativado" : (student.time || "Ausente")} 
        size="small" 
        color={isInactive ? "default" : cfg.badge} 
        variant={isInactive || student.status === 'Ausente' ? 'outlined' : 'filled'}
        sx={{ 
          height: 24, 
          fontSize: '0.7rem', 
          fontWeight: 'bold', 
          '& .MuiChip-icon': { ml: 0.5 },
          flexShrink: 0 
        }} 
      />
    </Paper>
  );
}